import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { API_BASE_URL } from '../App';
import {
  completeTotpEnrollment,
  completeTotpSignIn,
  prepareTotpEnrollment,
  signInFirstFactor,
} from '../services/firebaseAuth';

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState(localStorage.getItem('admin_email') || '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [resolver, setResolver] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      let credential;
      if (enrollment) {
        await completeTotpEnrollment(enrollment.user, enrollment.secret, code.trim());
        setEnrollment(null);
        setCode('');
        setMessage('Authenticator added. Sign in again to continue.');
        return;
      } else if (resolver) {
        credential = await completeTotpSignIn(resolver, code.trim());
      } else {
        const result = await signInFirstFactor(email.trim(), password);
        if (result.resolver) {
          setResolver(result.resolver);
          setPassword('');
          return;
        }
        credential = result.credential;
        const setup = await prepareTotpEnrollment(credential);
        if (setup) {
          setEnrollment({ ...setup, user: credential.user });
          setPassword('');
          setCode('');
          return;
        }
      }
      const idToken = await credential.user.getIdToken();
      const response = await axios.post(`${API_BASE_URL}/api/v1/admin/session`, { idToken });
      localStorage.setItem('admin_email', email);
      onLogin(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <LockOutlined sx={{ color: 'white' }} />
          </Box>
          
          <Typography component="h1" variant="h4" gutterBottom>
            Admin Login
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Access the YarnGPT Admin Dashboard
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {enrollment ? (
              <>
                <Typography variant="body2" align="center" sx={{ mb: 2 }}>
                  Scan this code with your authenticator app, then enter its six-digit code.
                </Typography>
                <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                  <QRCodeSVG value={enrollment.qrCodeUrl} size={180} />
                </Box>
                <Typography
                  variant="caption"
                  component="div"
                  align="center"
                  sx={{ overflowWrap: 'anywhere', mb: 1 }}
                >
                  Setup key: {enrollment.secretKey}
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="code"
                  label="Authenticator Code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  autoFocus
                />
              </>
            ) : resolver ? (
              <TextField
                margin="normal"
                required
                fullWidth
                name="code"
                label="Authenticator Code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
                autoFocus
              />
            ) : (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : enrollment ? (
                'Add Authenticator'
              ) : resolver ? (
                'Verify Code'
              ) : (
                'Continue'
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
